/**
 * TR808Panel - Interface complète de la TR-808
 * Design avec sections groupées par instrument
 */

import { themeManager } from '../themes/ThemeManager.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { TR808_PRESETS } from '../../state/InstrumentPresets.js';

export class TR808Panel {
    constructor(tr808, store, sequencer) {
        this.tr808 = tr808;
        this.store = store;
        this.sequencer = sequencer;
        this.panelId = 'tr808';

        // Références aux composants UI
        this.components = {
            knobs: {},
            stepButtons: {}
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('tr808');

        this.tr808.setPower(isPowered);

        container.innerHTML = `
            <div class="machine tr808 ${!isPowered ? 'powered-off' : ''} ${isCollapsed ? 'collapsed' : ''}" id="tr808_machine">
                <div class="machine-title tr808-title">
                    <button class="collapse-btn" id="tr808_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    TR-808 <span class="brand">Rhythm Composer</span>
                    <div id="tr808_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="tr808_power" title="Power ON/OFF">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="tr808_content">
                <div class="tr808-controls">
                    <!-- KICK Section -->
                    <div class="tr808-section">
                        <div class="section-title">KICK</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_kick_level"></div>
                            <div class="knob-group" id="tr808_kick_tone"></div>
                            <div class="knob-group" id="tr808_kick_decay"></div>
                        </div>
                    </div>

                    <!-- SNARE Section -->
                    <div class="tr808-section">
                        <div class="section-title">SNARE</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_snare_level"></div>
                            <div class="knob-group" id="tr808_snare_tone"></div>
                            <div class="knob-group" id="tr808_snare_snappy"></div>
                        </div>
                    </div>

                    <!-- CLAP Section -->
                    <div class="tr808-section">
                        <div class="section-title">CLAP</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_clap_level"></div>
                            <div class="knob-group" id="tr808_clap_decay"></div>
                        </div>
                    </div>

                    <!-- RIMSHOT Section -->
                    <div class="tr808-section">
                        <div class="section-title">RIMSHOT</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_rim_level"></div>
                            <div class="knob-group" id="tr808_rim_tune"></div>
                        </div>
                    </div>

                    <!-- HI-HATS Section -->
                    <div class="tr808-section">
                        <div class="section-title">HI-HATS</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_hh_level"></div>
                            <div class="knob-group" id="tr808_hh_decay"></div>
                            <div class="knob-group" id="tr808_ohh_level"></div>
                            <div class="knob-group" id="tr808_ohh_decay"></div>
                        </div>
                    </div>

                    <!-- TOMS Section -->
                    <div class="tr808-section toms-section">
                        <div class="section-title">TOMS</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_lotom_level"></div>
                            <div class="knob-group" id="tr808_lotom_tune"></div>
                            <div class="knob-group" id="tr808_midtom_level"></div>
                            <div class="knob-group" id="tr808_midtom_tune"></div>
                            <div class="knob-group" id="tr808_hitom_level"></div>
                            <div class="knob-group" id="tr808_hitom_tune"></div>
                        </div>
                    </div>

                    <!-- COWBELL Section -->
                    <div class="tr808-section">
                        <div class="section-title">COWBELL</div>
                        <div class="controls">
                            <div class="knob-group" id="tr808_cowbell_level"></div>
                        </div>
                    </div>
                </div>

                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${Array.from({length: 16}, (_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">KICK</span>
                        <div class="steps" id="kickSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">SNARE</span>
                        <div class="steps" id="snareSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">CLAP</span>
                        <div class="steps" id="clapSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">RIMSHOT</span>
                        <div class="steps" id="rimshotSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">HI-HAT</span>
                        <div class="steps" id="hihatSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">OPEN HH</span>
                        <div class="steps" id="openhhSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">LO TOM</span>
                        <div class="steps" id="lowtomSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">MID TOM</span>
                        <div class="steps" id="midtomSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">HI TOM</span>
                        <div class="steps" id="hitomSteps"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">COWBELL</span>
                        <div class="steps" id="cowbellSteps"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createKnobs();
            this.createDrumSteps();
            this.setupPresetSelector();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('tr808_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('tr808_power');
        const machine = document.getElementById('tr808_machine');

        powerBtn.addEventListener('click', () => {
            const isOn = this.tr808.getPower();
            const newState = !isOn;
            this.tr808.setPower(newState);
            this.store.setPowerState('tr808', newState);

            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    setupPresetSelector() {
        const container = document.getElementById('tr808_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: TR808_PRESETS,
            currentPreset: 'Classic 808',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        const params = this.tr808.getParams();

        // Apply preset parameters
        Object.keys(presetData).forEach(key => {
            const setter = `set${key.charAt(0).toUpperCase()}${key.slice(1)}`;
            if (typeof this.tr808[setter] === 'function') {
                this.tr808[setter](presetData[key]);
            }
        });

        // Update knobs
        if (presetData.kickDecay !== undefined) this.components.knobs.kickDecay?.setValue(presetData.kickDecay);
        if (presetData.kickTone !== undefined) this.components.knobs.kickTone?.setValue(presetData.kickTone);
        if (presetData.snareSnappy !== undefined) this.components.knobs.snareSnappy?.setValue(presetData.snareSnappy);
        if (presetData.hhDecay !== undefined) this.components.knobs.hhDecay?.setValue(presetData.hhDecay);

        this._saveParams();
    }

    createKnobs() {
        const params = this.tr808.getParams();

        // === KICK ===
        this.components.knobs.kickLevel = themeManager.createKnob(
            document.getElementById('tr808_kick_level'), {
                min: 0, max: 1, value: params.kickLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr808.setKickLevel(v); this._saveParams(); });

        this.components.knobs.kickTone = themeManager.createKnob(
            document.getElementById('tr808_kick_tone'), {
                min: 30, max: 80, value: params.kickTone,
                label: 'Tone', unit: 'Hz'
            }
        ).onChange(v => { this.tr808.setKickTone(v); this._saveParams(); });

        this.components.knobs.kickDecay = themeManager.createKnob(
            document.getElementById('tr808_kick_decay'), {
                min: 0.1, max: 1, value: params.kickDecay,
                label: 'Decay', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr808.setKickDecay(v); this._saveParams(); });

        // === SNARE ===
        this.components.knobs.snareLevel = themeManager.createKnob(
            document.getElementById('tr808_snare_level'), {
                min: 0, max: 1, value: params.snareLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr808.setSnareLevel(v); this._saveParams(); });

        this.components.knobs.snareTone = themeManager.createKnob(
            document.getElementById('tr808_snare_tone'), {
                min: 0, max: 1, value: params.snareTone,
                label: 'Tone', step: 0.01
            }
        ).onChange(v => { this.tr808.setSnareTone(v); this._saveParams(); });

        this.components.knobs.snareSnappy = themeManager.createKnob(
            document.getElementById('tr808_snare_snappy'), {
                min: 0, max: 1, value: params.snareSnappy,
                label: 'Snappy', step: 0.01
            }
        ).onChange(v => { this.tr808.setSnareSnappy(v); this._saveParams(); });

        // === CLAP ===
        this.components.knobs.clapLevel = themeManager.createKnob(
            document.getElementById('tr808_clap_level'), {
                min: 0, max: 1, value: params.clapLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr808.setClapLevel(v); this._saveParams(); });

        this.components.knobs.clapDecay = themeManager.createKnob(
            document.getElementById('tr808_clap_decay'), {
                min: 0.05, max: 0.4, value: params.clapDecay,
                label: 'Decay', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr808.setClapDecay(v); this._saveParams(); });

        // === RIMSHOT ===
        this.components.knobs.rimLevel = themeManager.createKnob(
            document.getElementById('tr808_rim_level'), {
                min: 0, max: 1, value: params.rimshotLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr808.setRimshotLevel(v); this._saveParams(); });

        this.components.knobs.rimTune = themeManager.createKnob(
            document.getElementById('tr808_rim_tune'), {
                min: 0, max: 1, value: params.rimshotTune,
                label: 'Tune', step: 0.01
            }
        ).onChange(v => { this.tr808.setRimshotTune(v); this._saveParams(); });

        // === HI-HATS ===
        this.components.knobs.hhLevel = themeManager.createKnob(
            document.getElementById('tr808_hh_level'), {
                min: 0, max: 1, value: params.hhLevel,
                label: 'CH Lvl', step: 0.01
            }
        ).onChange(v => { this.tr808.setHHLevel(v); this._saveParams(); });

        this.components.knobs.hhDecay = themeManager.createKnob(
            document.getElementById('tr808_hh_decay'), {
                min: 0.01, max: 0.3, value: params.hhDecay,
                label: 'CH Dec', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr808.setHihatDecay(v); this._saveParams(); });

        this.components.knobs.ohhLevel = themeManager.createKnob(
            document.getElementById('tr808_ohh_level'), {
                min: 0, max: 1, value: params.openHHLevel,
                label: 'OH Lvl', step: 0.01
            }
        ).onChange(v => { this.tr808.setOpenHHLevel(v); this._saveParams(); });

        this.components.knobs.ohhDecay = themeManager.createKnob(
            document.getElementById('tr808_ohh_decay'), {
                min: 0.1, max: 0.8, value: params.openHHDecay,
                label: 'OH Dec', unit: 's', step: 0.01, decimals: 2
            }
        ).onChange(v => { this.tr808.setOpenHHDecay(v); this._saveParams(); });

        // === TOMS ===
        this.components.knobs.loTomLevel = themeManager.createKnob(
            document.getElementById('tr808_lotom_level'), {
                min: 0, max: 1, value: params.lowTomLevel,
                label: 'Lo Lvl', step: 0.01
            }
        ).onChange(v => { this.tr808.setLowTomLevel(v); this._saveParams(); });

        this.components.knobs.loTomTune = themeManager.createKnob(
            document.getElementById('tr808_lotom_tune'), {
                min: 0, max: 1, value: params.lowTomTune,
                label: 'Lo Tune', step: 0.01
            }
        ).onChange(v => { this.tr808.setLowTomTune(v); this._saveParams(); });

        this.components.knobs.midTomLevel = themeManager.createKnob(
            document.getElementById('tr808_midtom_level'), {
                min: 0, max: 1, value: params.midTomLevel,
                label: 'Mid Lvl', step: 0.01
            }
        ).onChange(v => { this.tr808.setMidTomLevel(v); this._saveParams(); });

        this.components.knobs.midTomTune = themeManager.createKnob(
            document.getElementById('tr808_midtom_tune'), {
                min: 0, max: 1, value: params.midTomTune,
                label: 'Mid Tune', step: 0.01
            }
        ).onChange(v => { this.tr808.setMidTomTune(v); this._saveParams(); });

        this.components.knobs.hiTomLevel = themeManager.createKnob(
            document.getElementById('tr808_hitom_level'), {
                min: 0, max: 1, value: params.hiTomLevel,
                label: 'Hi Lvl', step: 0.01
            }
        ).onChange(v => { this.tr808.setHiTomLevel(v); this._saveParams(); });

        this.components.knobs.hiTomTune = themeManager.createKnob(
            document.getElementById('tr808_hitom_tune'), {
                min: 0, max: 1, value: params.hiTomTune,
                label: 'Hi Tune', step: 0.01
            }
        ).onChange(v => { this.tr808.setHiTomTune(v); this._saveParams(); });

        // === COWBELL ===
        this.components.knobs.cowbellLevel = themeManager.createKnob(
            document.getElementById('tr808_cowbell_level'), {
                min: 0, max: 1, value: params.cowbellLevel,
                label: 'Level', step: 0.01
            }
        ).onChange(v => { this.tr808.setCowbellLevel(v); this._saveParams(); });
    }

    createDrumSteps() {
        const drums = [
            { id: 'kickSteps', pattern: 'kick' },
            { id: 'snareSteps', pattern: 'snare' },
            { id: 'clapSteps', pattern: 'clap' },
            { id: 'rimshotSteps', pattern: 'rimshot' },
            { id: 'hihatSteps', pattern: 'hihat' },
            { id: 'openhhSteps', pattern: 'openhh' },
            { id: 'lowtomSteps', pattern: 'lowtom' },
            { id: 'midtomSteps', pattern: 'midtom' },
            { id: 'hitomSteps', pattern: 'hitom' },
            { id: 'cowbellSteps', pattern: 'cowbell' }
        ];

        drums.forEach(({ id, pattern }) => {
            const container = document.getElementById(id);
            this.components.stepButtons[pattern] = [];

            for (let i = 0; i < 16; i++) {
                const stepBtn = themeManager.createStepButton(container, {
                    index: i,
                    type: 'gate',
                    active: this.store.patterns[pattern][i]
                }).onChange(isActive => {
                    this.store.setStep(pattern, i, isActive);
                });
                this.components.stepButtons[pattern].push(stepBtn);
            }
        });
    }

    _saveParams() {
        this.store?.setSynthParams('tr808', this.tr808.getParams());
    }

    updateFromParams() {
        const params = this.tr808.getParams();
        const { knobs } = this.components;

        // Update all knobs
        if (knobs.kickLevel) knobs.kickLevel.setValue(params.kickLevel);
        if (knobs.kickTone) knobs.kickTone.setValue(params.kickTone);
        if (knobs.kickDecay) knobs.kickDecay.setValue(params.kickDecay);
        if (knobs.snareLevel) knobs.snareLevel.setValue(params.snareLevel);
        if (knobs.snareTone) knobs.snareTone.setValue(params.snareTone);
        if (knobs.snareSnappy) knobs.snareSnappy.setValue(params.snareSnappy);
        if (knobs.clapLevel) knobs.clapLevel.setValue(params.clapLevel);
        if (knobs.clapDecay) knobs.clapDecay.setValue(params.clapDecay);
        if (knobs.rimLevel) knobs.rimLevel.setValue(params.rimshotLevel);
        if (knobs.rimTune) knobs.rimTune.setValue(params.rimshotTune);
        if (knobs.hhLevel) knobs.hhLevel.setValue(params.hhLevel);
        if (knobs.hhDecay) knobs.hhDecay.setValue(params.hhDecay);
        if (knobs.ohhLevel) knobs.ohhLevel.setValue(params.openHHLevel);
        if (knobs.ohhDecay) knobs.ohhDecay.setValue(params.openHHDecay);
        if (knobs.loTomLevel) knobs.loTomLevel.setValue(params.lowTomLevel);
        if (knobs.loTomTune) knobs.loTomTune.setValue(params.lowTomTune);
        if (knobs.midTomLevel) knobs.midTomLevel.setValue(params.midTomLevel);
        if (knobs.midTomTune) knobs.midTomTune.setValue(params.midTomTune);
        if (knobs.hiTomLevel) knobs.hiTomLevel.setValue(params.hiTomLevel);
        if (knobs.hiTomTune) knobs.hiTomTune.setValue(params.hiTomTune);
        if (knobs.cowbellLevel) knobs.cowbellLevel.setValue(params.cowbellLevel);
    }

    destroy() {
        Object.values(this.components.knobs).forEach(knob => knob?.destroy());
        Object.values(this.components.stepButtons).forEach(buttons => {
            buttons.forEach(btn => btn?.destroy());
        });
    }
}
